// The highlight-clip recorder (docs/replays.md "Clip"): window.HEAVENSTEEL_HOST.clip
// records the Unity canvas with canvas.captureStream + MediaRecorder while the
// client plays the highlights, and on stop shows the result in a modal
// (preview, Download, Share with the file where the browser can, Copy link).
// HEAVENSTEEL_HOST.openClipUrl shows the same modal for a server-rendered mp4.
//
// Game audio: Unity's Web Audio player connects its nodes straight to the
// context's destination and exposes no master node, so the page taps those
// connections (AudioNode.prototype.connect, installed before Unity loads) and,
// while recording, also feeds them to a MediaStreamAudioDestinationNode whose
// track joins the video. If the tap can't be installed the clip is video only.
//
// The recorder's state goes to HEAVENSTEEL_STATUS.clip and a window
// "heavensteel:clip" event: {state: idle|recording|paused|stopping|ready|
// failed|closed, mime, audio, seconds, bytes, error}.

import {
	baseMime,
	clampFps,
	clipFilename,
	extensionFor,
	formatBytes,
	formatDuration,
	pageForClipUrl,
	pickMime,
} from "./clip-format";

/** The last clip shown (for the e2e, e2e/specs/replays.spec.ts). */
export type ClipResult =
	| { bytes: number; mime: string; seconds: number; filename: string }
	| { url: string; remote: true };

declare global {
	interface Window {
		HEAVENSTEEL_CLIP?: ClipResult;
	}
}

export type ClipState =
	| "idle"
	| "recording"
	| "paused"
	| "stopping"
	| "ready"
	| "failed"
	| "closed";

export interface ClipStatus {
	state: ClipState;
	mime: string | null;
	audio: boolean;
	seconds: number;
	bytes: number;
	error: string | null;
}

export interface ClipStopOptions {
	/** The download's name; the extension is set from what was recorded. */
	filename?: string;
	/** The share sheet's title. */
	title?: string;
	/** The replay link shared alongside (or instead of) the file. */
	url?: string;
}

export interface ClipHost {
	/** canvas.captureStream and MediaRecorder are both available. */
	supported(): boolean;
	/** Start recording the canvas; false when unsupported or already recording. */
	start(opts?: { fps?: number }): boolean;
	pause(): void;
	resume(): void;
	/** Stop and show the result (resolves true once the modal shows a clip). */
	stop(opts?: ClipStopOptions): Promise<boolean>;
	/** Stop and throw the recording away. */
	cancel(): void;
	/** The current state (also HEAVENSTEEL_STATUS.clip). */
	state(): ClipState;
}

export interface ClipDeps {
	canvas: HTMLCanvasElement;
	toast(message: string): void;
	copyText(text: string): Promise<boolean>;
	share(title: string, url: string): Promise<"shared" | "copied" | "failed">;
	setStatus(patch: Record<string, unknown>): void;
}

// 3.5 Mbps: a 20 s clip stays under 10 MB, the smallest common upload cap (measured: 6 Mbps made 15.4 MB)
const VIDEO_BPS = 3_500_000;
const AUDIO_BPS = 128_000;

// ── the audio tap ───────────────────────────────────────────────────────

type ConnectFn = (this: AudioNode, ...args: unknown[]) => unknown;

const tap = {
	installed: false,
	/** Nodes currently connected to an AudioDestinationNode (insertion order). */
	nodes: new Set<AudioNode>(),
	dest: null as MediaStreamAudioDestinationNode | null,
	active: false,
	connect: null as ConnectFn | null,
	disconnect: null as ConnectFn | null,
};
const TAP_MAX = 4000;

/** Install the tap before Unity creates its AudioContext. Safe to call twice. */
export function installAudioTap(): boolean {
	if (tap.installed) return true;
	try {
		if (
			typeof AudioNode === "undefined" ||
			typeof AudioDestinationNode === "undefined"
		)
			return false;
		const proto = AudioNode.prototype as unknown as {
			connect: ConnectFn;
			disconnect: ConnectFn;
		};
		const connect = proto.connect;
		const disconnect = proto.disconnect;
		tap.connect = connect;
		tap.disconnect = disconnect;
		proto.connect = function (this: AudioNode, ...args: unknown[]) {
			const out = connect.apply(this, args);
			if (args[0] instanceof AudioDestinationNode) {
				tap.nodes.add(this);
				if (tap.nodes.size > TAP_MAX) {
					const oldest = tap.nodes.values().next().value;
					if (oldest) tap.nodes.delete(oldest);
				}
				if (tap.active && tap.dest && this.context === tap.dest.context) {
					try {
						connect.call(this, tap.dest);
					} catch {}
				}
			}
			return out;
		};
		proto.disconnect = function (this: AudioNode, ...args: unknown[]) {
			const all = args.length === 0 || typeof args[0] === "number";
			const fromDest = args[0] instanceof AudioDestinationNode;
			if ((all || fromDest) && tap.nodes.delete(this) && fromDest) {
				if (tap.active && tap.dest) {
					try {
						disconnect.call(this, tap.dest);
					} catch {}
				}
			}
			return disconnect.apply(this, args);
		};
		tap.installed = true;
		return true;
	} catch {
		return false;
	}
}

/** Feed every tapped node into a stream destination; its audio track, or null. */
function startTap(): MediaStreamTrack | null {
	if (!tap.installed || !tap.connect || tap.nodes.size === 0) return null;
	let ctx: BaseAudioContext | null = null;
	for (const n of tap.nodes) ctx = n.context; // the newest node's context
	if (!ctx || !("createMediaStreamDestination" in ctx)) return null;
	try {
		const ac = ctx as AudioContext;
		if (!tap.dest || tap.dest.context !== ac)
			tap.dest = ac.createMediaStreamDestination();
		for (const n of tap.nodes) {
			if (n.context !== ac) continue;
			try {
				tap.connect.call(n, tap.dest);
			} catch {}
		}
		tap.active = true;
		return tap.dest.stream.getAudioTracks()[0] ?? null;
	} catch {
		return null;
	}
}

function stopTap(): void {
	if (!tap.active) return;
	tap.active = false;
	const dest = tap.dest;
	if (!dest || !tap.disconnect) return;
	for (const n of tap.nodes) {
		if (n.context !== dest.context) continue;
		try {
			tap.disconnect.call(n, dest);
		} catch {}
	}
}

// ── the modal ───────────────────────────────────────────────────────────

interface ModalContent {
	src: string;
	/** Revoke `src` on close (a recorded Blob). */
	objectUrl: boolean;
	file: File | null;
	filename: string;
	title: string;
	url: string | null;
	meta: string;
	kicker: string;
}

let modal: HTMLElement | null = null;
let current: ModalContent | null = null;
let deps: ClipDeps | null = null;
let lastFocus: Element | null = null;

function el<K extends keyof HTMLElementTagNameMap>(
	tag: K,
	attrs: Record<string, string> = {},
	text?: string,
): HTMLElementTagNameMap[K] {
	const e = document.createElement(tag);
	for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
	if (text !== undefined) e.textContent = text;
	return e;
}

function buildModal(): HTMLElement {
	const root = el("section", {
		id: "clip",
		class: "overlay soft clip-modal",
		role: "dialog",
		"aria-modal": "true",
		"aria-labelledby": "clip-title",
	});
	root.hidden = true;
	const card = el("div", { class: "clip-card" });
	const head = el("div", { class: "clip-head" });
	head.append(
		el("span", { class: "clip-kicker" }, "Highlight clip"),
		el("h2", { id: "clip-title", class: "clip-title" }),
	);
	const frame = el("div", { class: "clip-frame" });
	const video = el("video", {
		class: "clip-video",
		playsinline: "",
		muted: "",
		loop: "",
		autoplay: "",
		preload: "auto",
	});
	video.muted = true;
	frame.append(video);
	const meta = el("div", { class: "clip-meta" });
	const actions = el("div", { class: "clip-actions" });
	actions.append(
		el("a", { class: "btn primary", "data-clip": "download" }, "Download"),
		el(
			"button",
			{ type: "button", class: "btn", "data-clip": "share" },
			"Share",
		),
		el(
			"button",
			{ type: "button", class: "btn", "data-clip": "copy" },
			"Copy link",
		),
		el(
			"button",
			{ type: "button", class: "btn subtle", "data-clip": "close" },
			"Close",
		),
	);
	card.append(head, frame, meta, actions);
	root.append(card);

	root.addEventListener("click", (e) => {
		const target = e.target as HTMLElement;
		if (target === root) return closeModal();
		const action = target.closest("[data-clip]")?.getAttribute("data-clip");
		if (action === "close") closeModal();
		else if (action === "share") void shareCurrent();
		else if (action === "copy" && current?.url)
			void deps?.copyText(current.url);
	});
	// Unity listens for keys on the window: keep the modal's keys to itself.
	root.addEventListener("keydown", (e) => {
		e.stopPropagation();
		if (e.key === "Escape") {
			e.preventDefault();
			closeModal();
		} else if (e.key === "Tab") trapFocus(e);
	});
	root.addEventListener("keyup", (e) => e.stopPropagation());
	root.addEventListener("keypress", (e) => e.stopPropagation());
	video.addEventListener("loadedmetadata", () => {
		if (current && !current.meta && Number.isFinite(video.duration))
			setMeta(formatDuration(video.duration));
	});
	document.body.append(root);
	return root;
}

function trapFocus(e: KeyboardEvent): void {
	if (!modal) return;
	const items = [
		...modal.querySelectorAll<HTMLElement>("a[href], button:not([hidden])"),
	].filter((x) => !x.hidden);
	if (!items.length) return;
	const first = items[0];
	const last = items[items.length - 1];
	if (e.shiftKey && document.activeElement === first) {
		e.preventDefault();
		last.focus();
	} else if (!e.shiftKey && document.activeElement === last) {
		e.preventDefault();
		first.focus();
	}
}

function setMeta(text: string): void {
	const m = modal?.querySelector<HTMLElement>(".clip-meta");
	if (m) m.textContent = text;
}

function showModal(c: ModalContent): void {
	if (!modal) modal = buildModal();
	if (current) closeModal(true);
	current = c;
	lastFocus = document.activeElement;
	const q = <T extends HTMLElement>(s: string) => modal?.querySelector(s) as T;
	q<HTMLElement>(".clip-kicker").textContent = c.kicker;
	q<HTMLElement>(".clip-title").textContent = c.title;
	setMeta(c.meta);
	const video = q<HTMLVideoElement>(".clip-video");
	video.src = c.src;
	void video.play().catch(() => {});
	const dl = q<HTMLAnchorElement>('[data-clip="download"]');
	dl.href = c.src;
	dl.download = c.filename;
	const canShareFile =
		!!c.file &&
		typeof navigator.canShare === "function" &&
		(() => {
			try {
				return navigator.canShare({ files: [c.file as File] });
			} catch {
				return false;
			}
		})();
	q<HTMLButtonElement>('[data-clip="share"]').hidden = !canShareFile && !c.url;
	q<HTMLButtonElement>('[data-clip="copy"]').hidden = !c.url;
	modal.hidden = false;
	modal.classList.remove("done");
	deps?.setStatus({ clipModal: true });
	window.setTimeout(() => dl.focus(), 0);
}

function closeModal(replacing = false): void {
	if (!modal || !current) return;
	const video = modal.querySelector<HTMLVideoElement>(".clip-video");
	if (video) {
		video.pause();
		video.removeAttribute("src");
		video.load();
	}
	if (current.objectUrl) {
		const src = current.src;
		// Give a download that just started a moment to read the Blob.
		window.setTimeout(() => URL.revokeObjectURL(src), 60_000);
	}
	current = null;
	if (replacing) return;
	modal.hidden = true;
	deps?.setStatus({ clipModal: false });
	setClipStatus({ state: "closed" });
	// Hand the keyboard back to Unity.
	const canvas = deps?.canvas;
	if (canvas) canvas.focus();
	else if (lastFocus instanceof HTMLElement) lastFocus.focus();
}

async function shareCurrent(): Promise<void> {
	const c = current;
	if (!c || !deps) return;
	if (c.file && typeof navigator.share === "function") {
		let ok = false;
		try {
			ok = navigator.canShare?.({ files: [c.file] }) ?? false;
		} catch {
			ok = false;
		}
		if (ok) {
			try {
				// Some share targets drop the file when a url rides along: send the
				// link as text instead.
				await navigator.share({
					files: [c.file],
					title: c.title,
					text: c.url ? `${c.title} ${c.url}` : c.title,
				});
				return;
			} catch (e) {
				if ((e as DOMException)?.name === "AbortError") return;
			}
		}
	}
	if (c.url) await deps.share(c.title, c.url);
}

// ── the recorder ────────────────────────────────────────────────────────
// (Top-level names must not shadow window properties: the bundle runs as a
// classic script, where a top-level `var status` would be window.status, a string.)

let clipStatus: ClipStatus = {
	state: "idle",
	mime: null,
	audio: false,
	seconds: 0,
	bytes: 0,
	error: null,
};

function setClipStatus(patch: Partial<ClipStatus>): void {
	clipStatus = { ...clipStatus, ...patch };
	deps?.setStatus({ clip: clipStatus });
	window.dispatchEvent(
		new CustomEvent("heavensteel:clip", { detail: clipStatus }),
	);
}

interface Recording {
	recorder: MediaRecorder;
	stream: MediaStream;
	chunks: Blob[];
	mime: string;
	/** ms recorded before the current run (pauses excluded). */
	elapsed: number;
	runStart: number | null;
	cancelled: boolean;
	done: Promise<void>;
}

let rec: Recording | null = null;

function recordedSeconds(r: Recording): number {
	const run = r.runStart === null ? 0 : performance.now() - r.runStart;
	return (r.elapsed + run) / 1000;
}

function supported(): boolean {
	return (
		typeof MediaRecorder !== "undefined" &&
		typeof HTMLCanvasElement !== "undefined" &&
		typeof HTMLCanvasElement.prototype.captureStream === "function"
	);
}

function finish(r: Recording): void {
	for (const t of r.stream.getTracks()) {
		try {
			t.stop();
		} catch {}
	}
	stopTap();
	if (rec === r) rec = null;
}

function start(opts?: { fps?: number }): boolean {
	if (!deps || !supported()) {
		setClipStatus({ state: "failed", error: "unsupported" });
		return false;
	}
	if (rec) return false;
	const fps = clampFps(opts?.fps);
	let stream: MediaStream;
	try {
		stream = deps.canvas.captureStream(fps);
	} catch (e) {
		setClipStatus({ state: "failed", error: String(e) });
		return false;
	}
	const audio = startTap();
	if (audio) stream.addTrack(audio);
	const isSupported = (t: string) => MediaRecorder.isTypeSupported(t);
	const mime = pickMime(isSupported, !!audio);
	let recorder: MediaRecorder;
	try {
		recorder = new MediaRecorder(stream, {
			...(mime ? { mimeType: mime } : {}),
			videoBitsPerSecond: VIDEO_BPS,
			...(audio ? { audioBitsPerSecond: AUDIO_BPS } : {}),
		});
	} catch (e) {
		for (const t of stream.getTracks()) t.stop();
		stopTap();
		setClipStatus({ state: "failed", error: String(e) });
		return false;
	}
	const chunks: Blob[] = [];
	let resolveDone: () => void = () => {};
	const done = new Promise<void>((res) => {
		resolveDone = res;
	});
	const r: Recording = {
		recorder,
		stream,
		chunks,
		mime: recorder.mimeType || mime || "video/webm",
		elapsed: 0,
		runStart: performance.now(),
		cancelled: false,
		done,
	};
	recorder.ondataavailable = (e) => {
		if (e.data && e.data.size > 0) chunks.push(e.data);
	};
	recorder.onstop = () => resolveDone();
	recorder.onerror = (e) => {
		const err = (e as Event & { error?: DOMException }).error;
		setClipStatus({ state: "failed", error: err?.message ?? "recorder error" });
		resolveDone();
	};
	try {
		recorder.start(1000);
	} catch (e) {
		finish(r);
		setClipStatus({ state: "failed", error: String(e) });
		return false;
	}
	rec = r;
	setClipStatus({
		state: "recording",
		mime: r.mime,
		audio: !!audio,
		seconds: 0,
		bytes: 0,
		error: null,
	});
	return true;
}

function pause(): void {
	const r = rec;
	if (!r || r.recorder.state !== "recording") return;
	try {
		r.recorder.pause();
	} catch {
		return;
	}
	if (r.runStart !== null) r.elapsed += performance.now() - r.runStart;
	r.runStart = null;
	setClipStatus({ state: "paused", seconds: recordedSeconds(r) });
}

function resume(): void {
	const r = rec;
	if (!r || r.recorder.state !== "paused") return;
	try {
		r.recorder.resume();
	} catch {
		return;
	}
	r.runStart = performance.now();
	setClipStatus({ state: "recording" });
}

async function stop(opts?: ClipStopOptions): Promise<boolean> {
	const r = rec;
	if (!r) return false;
	const seconds = recordedSeconds(r);
	if (r.runStart !== null) r.elapsed += performance.now() - r.runStart;
	r.runStart = null;
	setClipStatus({ state: "stopping", seconds });
	try {
		if (r.recorder.state !== "inactive") r.recorder.stop();
	} catch {}
	await Promise.race([
		r.done,
		new Promise((res) => window.setTimeout(res, 5000)),
	]);
	finish(r);
	if (r.cancelled) return false;
	const type = baseMime(r.mime);
	const blob = new Blob(r.chunks, { type });
	if (!blob.size) {
		setClipStatus({ state: "failed", error: "empty recording", bytes: 0 });
		deps?.toast("The clip came out empty. Try again.");
		return false;
	}
	const filename = clipFilename(opts?.filename, type);
	let file: File | null = null;
	try {
		file = new File([blob], filename, { type });
	} catch {
		file = null;
	}
	const title = opts?.title?.trim() || "Heavensteel highlight";
	const ext = extensionFor(type).toUpperCase();
	setClipStatus({ state: "ready", seconds, bytes: blob.size });
	window.HEAVENSTEEL_CLIP = { bytes: blob.size, mime: type, seconds, filename };
	showModal({
		src: URL.createObjectURL(blob),
		objectUrl: true,
		file,
		filename,
		title,
		url: opts?.url?.trim() || null,
		meta: [formatDuration(seconds), formatBytes(blob.size), ext]
			.filter(Boolean)
			.join(" · "),
		kicker: "Highlight clip",
	});
	return true;
}

function cancel(): void {
	const r = rec;
	if (!r) return;
	r.cancelled = true;
	try {
		if (r.recorder.state !== "inactive") r.recorder.stop();
	} catch {}
	finish(r);
	setClipStatus({ state: "idle", seconds: 0, bytes: 0 });
}

/** Show the modal for a remote mp4 (the server-rendered fallback). */
export function openClipUrl(url: string, title?: string): void {
	if (!url) return;
	let abs: string;
	try {
		abs = new URL(url, location.href).toString();
	} catch {
		return;
	}
	const code = /\/r\/([^/]+)\/clip\.mp4/.exec(new URL(abs).pathname)?.[1];
	const page = pageForClipUrl(abs);
	window.HEAVENSTEEL_CLIP = { url: abs, remote: true };
	showModal({
		src: abs,
		objectUrl: false,
		file: null,
		filename: clipFilename(
			code ? `heavensteel-${code}` : "heavensteel-clip",
			"video/mp4",
		),
		title: title?.trim() || "Heavensteel highlight",
		url: page,
		meta: "",
		kicker: "Highlight clip",
	});
}

export function createClipHost(d: ClipDeps): ClipHost {
	deps = d;
	return {
		supported,
		start,
		pause,
		resume,
		stop,
		cancel,
		state: () => clipStatus.state,
	};
}
